import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, BookOpen, Trophy, Images, MessageSquareQuote, Youtube, Instagram, Award, Settings, LogOut, GraduationCap, Menu, ExternalLink, Star, CalendarClock, Medal, IndianRupee, CalendarCheck, UserCheck, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import EnquiriesPanel from "@/pages/admin/EnquiriesPanel";
import SitePanel from "@/pages/admin/SitePanel";
import CrudPanel from "@/pages/admin/CrudPanel";
import StarsPanel from "@/pages/admin/StarsPanel";
import AttendancePanel from "@/pages/admin/AttendancePanel";
import StarTeacherPanel from "@/pages/admin/StarTeacherPanel";
import FeesPanel from "@/pages/admin/FeesPanel";
import AdminStudentAttendance from "@/pages/admin/AdminStudentAttendance";
import AdminActivations from "@/pages/admin/AdminActivations";
import StudentsPanel from "@/pages/admin/StudentsPanel";
import DiaryFeedbackPanel from "@/pages/admin/DiaryFeedbackPanel";
import { Heart, Megaphone } from "lucide-react";
import MarketingPanel from "@/pages/admin/marketing/MarketingPanel";
import Messages, { useUnreadMessages } from "@/pages/star/Messages";

const NAV = [
  { key: "enquiries", label: "Enquiries", icon: Inbox },
  { key: "marketing", label: "AI Marketing", icon: Megaphone },
  { key: "messages", label: "Messages", icon: MessageCircle },
  { key: "students", label: "Students", icon: GraduationCap },
  { key: "stars", label: "Star System", icon: Star },
  { key: "activations", label: "Activations", icon: UserCheck },
  { key: "fees", label: "Fees", icon: IndianRupee },
  { key: "attendance", label: "Attendance", icon: CalendarClock },
  { key: "studentattendance", label: "Student Attendance", icon: CalendarCheck },
  { key: "diaryfeedback", label: "Parent Feedback", icon: Heart },
  { key: "starteacher", label: "Star Teacher", icon: Medal },
  { key: "courses", label: "Courses", icon: BookOpen },
  { key: "results", label: "Results", icon: Trophy },
  { key: "achievements", label: "Std 9 Achievements", icon: Award },
  { key: "gallery", label: "Gallery", icon: Images },
  { key: "testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { key: "videos", label: "Videos", icon: Youtube },
  { key: "reels", label: "Instagram Reels", icon: Instagram },
  { key: "site", label: "Contact Info", icon: Settings },
];

const CRUD = {
  courses: {
    title: "Courses", endpoint: "courses", primaryField: "title", subField: "subtitle",
    empty: { title: "", subtitle: "", description: "", subjects: "", levels: "", methodology: "", batch_info: "", fees: "", order: 0, active: true },
    fields: [
      { name: "title", label: "Course Title", type: "text", placeholder: "School Tuition" },
      { name: "subtitle", label: "Subtitle", type: "text", placeholder: "Pre-Primary to Secondary" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "levels", label: "Education Levels / What We Cover (one per line)", type: "lines", hint: "One per line, e.g. 'Pre-Primary: Nursery, Jr. KG & Sr. KG'" },
      { name: "methodology", label: "Teaching Methodology", type: "textarea" },
      { name: "batch_info", label: "Batch Information", type: "text" },
      { name: "fees", label: "Fees (leave blank to hide)", type: "text" },
      { name: "order", label: "Display Order", type: "number" },
    ],
  },
  results: {
    title: "Student Progress", endpoint: "progress", primaryField: "student_name", subField: "after_label", thumbField: "after_doc",
    empty: { student_name: "", before_label: "", before_percentage: "", after_label: "", after_percentage: "", before_doc: "", after_doc: "", mode: "percentage", highlight: "", highlight_before: "", highlight_after: "", order: 0 },
    fields: [
      { name: "student_name", label: "Student Name", type: "text" },
      { name: "mode", label: "Card Mode", type: "select", options: ["percentage", "grade"] },
      { name: "before_label", label: "Before — Label", type: "text", placeholder: "Standard 9" },
      { name: "before_percentage", label: "Before — Percentage (percentage mode)", type: "text", placeholder: "80.83%" },
      { name: "before_doc", label: "Before — Result Document", type: "image" },
      { name: "after_label", label: "After — Label", type: "text", placeholder: "SSC" },
      { name: "after_percentage", label: "After — Percentage (percentage mode)", type: "text", placeholder: "90.60%" },
      { name: "after_doc", label: "After — Result Document", type: "image" },
      { name: "highlight", label: "Grade Highlight — Subject (grade mode)", type: "text", placeholder: "Mathematics" },
      { name: "highlight_before", label: "Grade Highlight — Before", type: "text", placeholder: "A2" },
      { name: "highlight_after", label: "Grade Highlight — After", type: "text", placeholder: "A1" },
      { name: "order", label: "Display Order", type: "number" },
    ],
  },
  gallery: {
    title: "Gallery", endpoint: "gallery", primaryField: "caption", subField: "category", thumbField: "image",
    empty: { image: "", caption: "", category: "Celebrations", order: 0 },
    fields: [
      { name: "image", label: "Photo", type: "image" },
      { name: "caption", label: "Caption", type: "text", placeholder: "e.g. Annual day celebration 2025" },
      { name: "category", label: "Category", type: "select", options: ["Pre-Primary & Primary", "School Students", "Classroom Learning", "Commerce & Degree", "Celebrations", "PTG Family"] },
      { name: "order", label: "Display Order", type: "number" },
    ],
  },
  testimonials: {
    title: "Testimonials", endpoint: "testimonials", primaryField: "name", subField: "role", thumbField: "photo",
    empty: { name: "", role: "Parent", standard: "", year: "", text: "", photo: "", order: 0 },
    fields: [
      { name: "photo", label: "Photo (optional)", type: "image" },
      { name: "name", label: "Name", type: "text" },
      { name: "role", label: "Student / Parent", type: "text", placeholder: "Parent" },
      { name: "standard", label: "Standard / Class", type: "text" },
      { name: "year", label: "Year", type: "text" },
      { name: "text", label: "Testimonial", type: "textarea" },
      { name: "order", label: "Display Order", type: "number" },
    ],
  },
  videos: {
    title: "Videos", endpoint: "videos", primaryField: "title", subField: "description", thumbField: "youtube_id",
    empty: { title: "", youtube_id: "", description: "", order: 0 },
    fields: [
      { name: "title", label: "Video Title", type: "text" },
      { name: "youtube_id", label: "YouTube Video ID", type: "text", hint: "From youtube.com/watch?v=XXXX — enter only the XXXX part" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "order", label: "Display Order", type: "number" },
    ],
  },
  reels: {
    title: "Instagram Reels", endpoint: "reels", primaryField: "url", subField: "order",
    empty: { url: "", order: 0 },
    fields: [
      { name: "url", label: "Instagram Reel URL", type: "text", hint: "e.g. https://www.instagram.com/reel/XXXXXXXXXXX/" },
      { name: "order", label: "Display Order", type: "number" },
    ],
  },
  achievements: {
    title: "Std 9 Achievements", endpoint: "achievements", primaryField: "student_name", subField: "label", thumbField: "doc",
    empty: { student_name: "", label: "Standard 9 Result", percentage: "", school: "", subjects: "", note: "", doc: "", order: 0 },
    fields: [
      { name: "student_name", label: "Student Name", type: "text" },
      { name: "label", label: "Label", type: "text", placeholder: "Standard 9 Result" },
      { name: "percentage", label: "Percentage (leave blank if none)", type: "text", placeholder: "85%" },
      { name: "school", label: "School (optional)", type: "text" },
      { name: "subjects", label: "Subject Marks (one per line)", type: "lines", hint: "e.g. Maths I: 18/20" },
      { name: "note", label: "Note (optional)", type: "text" },
      { name: "doc", label: "Result Document (optional)", type: "image" },
      { name: "order", label: "Display Order", type: "number" },
    ],
  },
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("enquiries");
  const [drawer, setDrawer] = useState(false);
  const [pendingActs, setPendingActs] = useState(0);
  const unreadMsgs = useUnreadMessages();

  const loadCount = useCallback(async () => {
    try { const { data } = await api.get("/admin/activation-pending-count"); setPendingActs(data.pending_admin || 0); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { loadCount(); }, [loadCount, active]);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const renderPanel = () => {
    if (active === "enquiries") return <EnquiriesPanel />;
    if (active === "messages") return <Messages scope="admin" />;
    if (active === "marketing") return <MarketingPanel />;
    if (active === "students") return <StudentsPanel />;
    if (active === "stars") return <StarsPanel />;
    if (active === "activations") return <AdminActivations onChange={loadCount} />;
    if (active === "fees") return <FeesPanel />;
    if (active === "attendance") return <AttendancePanel />;
    if (active === "studentattendance") return <AdminStudentAttendance />;
    if (active === "diaryfeedback") return <DiaryFeedbackPanel />;
    if (active === "starteacher") return <StarTeacherPanel />;
    if (active === "site") return <SitePanel />;
    return <CrudPanel {...CRUD[active]} />;
  };

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-3 px-2 mb-8">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-800 text-white"><GraduationCap className="h-6 w-6" /></div>
        <div><div className="font-heading font-extrabold text-white">PTG Admin</div><div className="text-[10px] text-sky-400 uppercase tracking-widest">Control Panel</div></div>
      </div>
      <nav className="space-y-1 flex-1 min-h-0 overflow-y-auto -mr-2 pr-2" data-testid="admin-sidebar-nav">
        {NAV.map((n) => (
          <button key={n.key} onClick={() => { setActive(n.key); setDrawer(false); }} data-testid={`admin-nav-${n.key}`}
            className={`group w-full flex items-center gap-3 px-2.5 py-2 rounded-xl text-sm font-semibold transition-all ${active === n.key ? "bg-gradient-to-r from-blue-700 to-sky-600 text-white shadow-md shadow-blue-900/30" : "text-slate-300 hover:bg-white/5"}`}>
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${active === n.key ? "bg-white/20 text-white" : "bg-slate-800/70 text-slate-300 group-hover:text-white"}`}><n.icon className="h-4 w-4" /></span>
            <span className="flex-1 text-left">{n.label}</span>
            {n.key === "activations" && pendingActs > 0 && (
              <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold" data-testid="activations-badge">{pendingActs}</span>
            )}
            {n.key === "messages" && unreadMsgs > 0 && (
              <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-blue-500 text-white text-[11px] font-bold" data-testid="messages-nav-badge">{unreadMsgs}</span>
            )}
          </button>
        ))}
      </nav>
      <div className="space-y-1 pt-4 border-t border-slate-800">
        <a href="/" target="_blank" rel="noreferrer" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-slate-300 hover:bg-slate-800" data-testid="admin-view-site"><ExternalLink className="h-4.5 w-4.5" /> View Website</a>
        <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-red-400 hover:bg-slate-800" data-testid="admin-logout"><LogOut className="h-4.5 w-4.5" /> Logout</button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-slate-100 overflow-hidden" data-testid="admin-dashboard">
      <Seo title="Admin Dashboard" />
      <aside className="hidden lg:flex flex-col w-64 bg-gradient-to-b from-slate-950 via-blue-950 to-indigo-950 p-4 shrink-0"><SidebarContent /></aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="lg:hidden flex items-center justify-between bg-gradient-to-r from-slate-950 via-blue-950 to-indigo-950 p-4">
          <div className="flex items-center gap-2 text-white"><GraduationCap className="h-5 w-5" /><span className="font-heading font-bold">PTG Admin</span></div>
          <Sheet open={drawer} onOpenChange={setDrawer}>
            <SheetTrigger asChild><Button variant="ghost" size="icon" className="text-white" data-testid="admin-menu-btn"><Menu className="h-5 w-5" /></Button></SheetTrigger>
            <SheetContent side="left" className="w-64 bg-gradient-to-b from-slate-950 via-blue-950 to-indigo-950 border-slate-800 p-4 flex flex-col"><SheetTitle className="sr-only">PTG Admin navigation</SheetTitle><SidebarContent /></SheetContent>
          </Sheet>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-8">
          <div className="mb-6 flex items-center gap-3" data-testid="admin-welcome">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-blue-800 to-sky-600 text-white text-base font-extrabold shadow-md shadow-blue-900/20">{(user?.email || "A").charAt(0).toUpperCase()}</div>
            <div className="min-w-0">
              <p className="text-xs text-slate-500">Welcome back,</p>
              <p className="text-base font-extrabold text-slate-900 leading-tight truncate">{user?.email}</p>
            </div>
          </div>
          {renderPanel()}
        </main>
      </div>
    </div>
  );
}
